/**
 * The landing page's educational layer — plan §4's "light educational layer",
 * which §14.4 flags as the item most likely to be squeezed out. It was.
 *
 * **Who this is for.** Someone who has heard of model weights and has never
 * looked at any. The register the owner asked for is "accessible for the
 * general audience while technically interesting for the hard core scientist" —
 * both at once, not a compromise. So: no jargon without its meaning attached,
 * and no simplification that a specialist would have to unlearn. Every number
 * here is real and checkable.
 *
 * **Lead with why, not what.** A visitor who does not already care what a
 * tensor is will not be persuaded by a definition of one. The order is:
 * something surprising is true → here is what the words mean → here is why
 * anyone bothers. The mechanism (a JSON header at the front of the file) is
 * genuinely interesting but it is the *last* thing that matters to a newcomer,
 * so it comes last.
 *
 * **Progressive disclosure.** Two short paragraphs are always visible; the
 * deeper material sits behind a disclosure. That serves both readers without
 * making either read the other's version — an expert can skip straight to the
 * examples, and a curious newcomer can keep going without hunting elsewhere.
 *
 * That now applies below the fold too. The two sections under the examples used
 * to be a two-column grid of always-open prose, which made the landing page's
 * last screen its densest — the reverse of the order everything above it is in.
 * They are `<details>` now, in one column, matching the disclosure above the
 * buttons: same element, same styling, same cost to open.
 *
 * Pure: returns HTML, touches no DOM, holds no state.
 */

/**
 * The always-visible introduction.
 *
 * Two paragraphs, deliberately. The first says what a model *is* in terms of
 * things a non-specialist already has words for; the second says why anyone
 * would open one up. Neither mentions safetensors, HTTP, or this tool — those
 * are answers to questions the reader has not asked yet.
 */
export function introHtml(): string {
  return `
    <div class="intro prose">
      <p>
        A trained model is, quite literally, a large pile of numbers. Every
        <em>weight</em> is one number the model settled on during training, and
        a modern one has billions of them, organised into named blocks called
        <em>tensors</em> &mdash; one for each part of the network that learned
        something. Nothing else is in there. No rules, no code, no text: just
        the numbers and the shape they are arranged in.
      </p>
      <p>
        Those numbers have structure, and the structure is legible. You can see
        where a model keeps most of its parameters, which layers were trained
        differently from their neighbours, and&mdash;most usefully&mdash;whether
        a handful of unusually large weights are hiding in a tensor. That last
        one decides whether a model survives being made smaller, which is
        currently the difference between a model that runs on a laptop and one
        that needs a datacentre.
      </p>
    </div>`;
}

/**
 * The optional second layer.
 *
 * Behind a disclosure rather than always-on, because a reader who already knows
 * what a heavy tail is should not have to scroll past an explanation of it, and
 * a reader who does not should not have to leave to find one. `<details>` and
 * not a modal or a link out: it costs one click, keeps the reader on the page,
 * is keyboard-operable for free, and its content is in the DOM for search
 * engines and screen readers whether or not it is open.
 *
 * The claims here are the ones the tool can actually demonstrate on real
 * models, which is the constraint that keeps this from becoming marketing. If
 * a sentence could not be checked by loading a repo and looking, it does not
 * belong.
 */
function deeperHtml(): string {
  return `
    <details class="intro-more">
      <summary>Why anyone makes models smaller, and what that costs</summary>
      <div class="intro-more-body">
        <p>
          Weights are usually trained and stored at 16 bits each. A 7-billion
          parameter model at 16 bits is about 14&nbsp;GB &mdash; too big for
          most laptops and every phone. Store the same weights at 4 bits and it
          is closer to 4&nbsp;GB, which fits. That single change is most of what
          separates &ldquo;send your text to somebody else&rsquo;s server&rdquo;
          from &ldquo;runs on the machine in front of you&rdquo;, and with it
          go the things that follow from running locally: it works offline, it
          costs nothing per use, and whatever you type never leaves your
          computer.
        </p>
        <p>
          The catch is that 4 bits only gives you sixteen possible values per
          weight. Making that work means storing a small group of weights
          together with a shared scaling factor, so the sixteen values stretch
          to cover whatever range that group happens to need. It works well
          &mdash; right up until one weight in the group is a hundred times
          larger than the rest. Then the scale stretches to reach the outlier
          and every other weight in the group collapses onto the same few
          values. The model does not crash. It just gets quietly worse, in ways
          that are hard to attribute after the fact.
        </p>
        <p>
          This is why looking matters, and why the interesting statistic is
          usually not the average. The average weight tells you very little; the
          <em>ratio between the typical weight and the extreme one</em> tells
          you whether a tensor is going to survive compression. TensorLens
          computes that for any tensor you open, next to the distribution it
          came from, so the reason is visible and not just the verdict.
        </p>
        <p class="intro-close">
          The mechanism, for the curious: a safetensors file begins with a JSON
          index describing every tensor &mdash; its name, shape, type, and
          exactly which bytes it occupies. Reading that index is two small
          requests, whatever the file's size, which is why a 200&nbsp;GB model
          opens about as fast as a 200&nbsp;MB one. Open a single tensor and
          only that tensor's bytes are fetched. The model itself stays where it
          is.
        </p>
      </div>
    </details>`;
}

/**
 * The two sections below the examples.
 *
 * **&ldquo;What you <em>will</em> be looking at&rdquo;, not &ldquo;what you are
 * looking at&rdquo;.** This copy only ever renders on the empty state, where
 * the visitor has loaded nothing and is looking at an illustration of a robot.
 * The present tense described a screen that was not on screen, which is a
 * correctness point rather than a wording preference: the sentence that follows
 * it promises an anatomy map the reader cannot see yet.
 *
 * Same `<details class="intro-more">` shape as `deeperHtml`, deliberately — one
 * disclosure style on the page, and a reader who has already opened the one
 * above the buttons knows exactly what these two do. Order is the reader's
 * order: what the thing is, then why to bother.
 */
export function landingNotesHtml(): string {
  return `
    <div class="landing-notes">
      <details class="intro-more">
        <summary>What you will be looking at</summary>
        <div class="intro-more-body">
          <p>
            Every tensor has a shape, a numeric type, and a size on disk. Read
            together they describe the architecture: how many layers, how wide
            the attention projections are, how much of the model is the
            embedding table. The anatomy map lays all of it out at once, one
            column per layer, so the structure is a picture rather than a list
            of 272 names.
          </p>
        </div>
      </details>
      <details class="intro-more">
        <summary>Why it is worth a look</summary>
        <div class="intro-more-body">
          <p>
            Models are often shipped smaller than they were trained.
            <em>Quantization</em> stores the same weights in fewer bits &mdash;
            4 instead of 16, say &mdash; which is largely what decides whether a
            model runs on a laptop or needs a datacentre GPU. That trade is not
            applied evenly: some tensors are squeezed hard, others are kept at
            full precision because squeezing them costs too much accuracy. Which
            is which shows up directly in the layout, and you can read it off
            any public model here.
          </p>
        </div>
      </details>
      ${deeperHtml()}
    </div>`;
}
